<script setup lang="ts">
/**
 * Full-screen survey chrome, shared by the size and taste surveys.
 *
 * Owns only the frame the two have in common — exit control, title, progress,
 * content area, footer — in the redesign's flat `--fair-rd-*` language. Step
 * bodies and the button set stay with the feature: they differ per survey and
 * per step, and the two wrappers already encode those rules.
 */
import { computed } from 'vue'
import Dialog from 'primevue/dialog'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import './fair-survey-dialog.css'
import './fair-survey-step.css'

interface FairSurveyDialogProps {
  open?: boolean
  /** Shown centred on the welcome step; elsewhere the progress row carries it. */
  title?: string
  showTitle?: boolean
  exitLabel?: string
  showProgress?: boolean
  /** 0…100 — the caller decides whether it counts steps or swiped cards. */
  progressPercent?: number
  progressLabel?: string
  loading?: boolean
  loadingLabel?: string
  showFooter?: boolean
  savedLabel?: string
  showSaved?: boolean
  compact?: boolean
}

const props = withDefaults(defineProps<FairSurveyDialogProps>(), {
  open: false,
  title: '',
  showTitle: false,
  exitLabel: 'Сохранить и выйти',
  showProgress: false,
  progressPercent: 0,
  progressLabel: '',
  loading: false,
  loadingLabel: 'Загрузка…',
  showFooter: true,
  savedLabel: 'Ответы сохранены',
  showSaved: false,
  compact: false,
})

const emit = defineEmits<{
  'update:open': [value: boolean]
  close: []
}>()

const clampedPercent = computed(() =>
  Math.max(0, Math.min(100, props.progressPercent)),
)

function handleClose() {
  emit('update:open', false)
  emit('close')
}
</script>

<template>
  <Dialog
    :visible="open"
    modal
    :closable="false"
    :show-header="false"
    :pt="{
      root: { class: 'fair-survey-dialog__root' },
      mask: { class: 'fair-survey-dialog__mask' },
    }"
    @update:visible="emit('update:open', $event)"
    @hide="emit('close')"
  >
    <template #container>
      <div
        class="fair-survey-dialog"
        :class="{ 'fair-survey-dialog--compact': compact }"
      >
        <header class="fair-survey-dialog__header">
          <div class="fair-survey-dialog__bar">
            <button
              type="button"
              class="fair-survey-dialog__exit"
              @click="handleClose"
            >
              {{ exitLabel }}
            </button>

            <span
              v-if="showTitle && title"
              class="fair-survey-dialog__title"
            >
              {{ title }}
            </span>

            <FairIconButton
              class="fair-survey-dialog__close"
              icon="close"
              :size="compact ? 'mobile' : 'medium'"
              aria-label="Закрыть"
              @click="handleClose"
            />
          </div>

          <!-- Progress is a hairline rail: it informs without competing with the step. -->
          <div
            v-if="showProgress"
            class="fair-survey-dialog__progress"
          >
            <div
              class="fair-survey-dialog__progress-track"
              role="progressbar"
              :aria-valuenow="clampedPercent"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <span
                class="fair-survey-dialog__progress-fill"
                :style="{ width: `${clampedPercent}%` }"
              />
            </div>
            <span
              v-if="progressLabel"
              class="fair-survey-dialog__progress-label"
            >
              {{ progressLabel }}
            </span>
          </div>
        </header>

        <div class="fair-survey-dialog__content">
          <div
            v-if="loading"
            class="fair-survey-dialog__loading"
          >
            {{ loadingLabel }}
          </div>
          <slot v-else />
        </div>

        <footer
          v-if="showFooter"
          class="fair-survey-dialog__footer"
        >
          <span
            v-if="showSaved"
            class="fair-survey-dialog__saved"
          >
            {{ savedLabel }}
          </span>
          <div class="fair-survey-dialog__actions">
            <slot name="actions" />
          </div>
        </footer>
      </div>
    </template>
  </Dialog>
</template>
